import React, { FC, useContext, useMemo, useState } from 'react'

import { useParams } from 'react-router'
import { cloneDeep } from 'lodash'

import InfiniteScrollDataTable from '../../../../../../../components/common/tables/InfiniteScrollDataTable'

import { IBreakRegistrationByDay } from '../../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'

import { getColumnsScoring } from './data/columns'

import { IRegistrationByDayEx, RegistrationFeesTypeEx } from '../../ManageClinicNOtherOperationsTab'
import { convertTo12HourFormat } from '../../../../../../../helpers/time'
import { sortByTotalPoints } from '../../data/operations-tab-utils'
import { HandleModalContext } from '../../../../../../../layout/mainlayout/MainLayout'
import { useEventTrainers } from '../../../../../../../hooks/useEventTrainers'
import { onRank } from './data/scoring-utils'

import { MODAL_CONSTS } from '../../../../../../../const/modal-const'
import { IManageOption } from '../../types/IManageOption'

interface ScoringTableByRiderProps {
  selectedDay: IManageOption
  registrationsGroupedByDay: { [key: string]: IBreakRegistrationByDay[] }
  registrationsByDay: IRegistrationByDayInterface[] | null
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  getRegistrationsByDay: () => Promise<IRegistrationByDayInterface[]>
  onChangeJudge: (
    row: RegistrationFeesTypeEx,
    values: { label: string; value: string }[]
  ) => Promise<void>
}

export const ScoringTableByRider: FC<ScoringTableByRiderProps> = ({
  selectedDay,
  registrationsByDay,
  setRegistrationsByDay,
  registrationsGroupedByDay,
  getRegistrationsByDay,
  onChangeJudge,
}) => {
  const { eventId } = useParams<{ eventId: string }>()

  const { eventTrainers } = useEventTrainers(eventId)

  const handleModalContext = useContext(HandleModalContext)

  const [riderModeIsAsc, setRiderModeIsAsc] = useState(false)
  const [scoringLoading, setScoringLoading] = useState(false)

  const registrationsToDisplay = useMemo(() => {
    let registrations = selectedDay ? [...(registrationsGroupedByDay[selectedDay.value] || [])] : []

    const registrationsByClass: Record<string, IRegistrationByDayInterface[]> = {}

    registrations.forEach((reg) => {
      const className = reg.registrationByDayName || ''
      if (!registrationsByClass[className]) {
        registrationsByClass[className] = []
      }
      registrationsByClass[className].push(reg as IRegistrationByDayInterface)
    })

    let sortedRegistrations: IRegistrationByDayInterface[] = []

    Object.entries(registrationsByClass).forEach(([_, classRegs]) => {
      // Sort by total points
      let sorted = sortByTotalPoints(cloneDeep(classRegs) ?? [], riderModeIsAsc ? 'asc' : 'desc')

      let count = 0

      // update rank for all registrations that have the same class
      sorted = sorted?.map((registrationByDay) => {
        count++
        return {
          ...registrationByDay,
          ...(registrationByDay.score && {
            score: { ...registrationByDay.score, rank: count },
          }),
        }
      })

      sortedRegistrations.push(...sorted)
    })

    return sortedRegistrations
  }, [selectedDay, registrationsGroupedByDay, riderModeIsAsc])

  const onSortScore = () => {
    setRiderModeIsAsc(!riderModeIsAsc)
  }

  const onScore = (row: IRegistrationByDayEx) => {
    if (row.score?.sheet?.type === 'hunter-jumper') {
      handleModalContext?.handleModal(true, MODAL_CONSTS.CUSTOM_SCORING, {
        modalTitle: 'Results',
        rider: row,
        onSaveScoring: () => getRegistrationsByDay(),
        editable: true,
      })
    } else {
      handleModalContext?.handleModal(true, MODAL_CONSTS.SCORING, {
        rider: row,
        onSaveScoring: () => getRegistrationsByDay(),
        editable: true,
      })
    }
  }

  const getTime = ({ rideTime }: { rideTime: string | null; index: number }) => {
    if (rideTime) {
      const { hour, minute, second, period } = convertTo12HourFormat(rideTime)
      return `${hour}:${minute}:${second} ${period}`
    } else {
      return '00:00:00 AM'
    }
  }

  return (
    <InfiniteScrollDataTable
      hasMore={false}
      className="exhibitorListTable"
      columns={getColumnsScoring({
        type: 'rider',
        eventTrainers,
        scoringLoading,
        registrationsByDay,
        setRegistrationsByDay,
        getTime,
        onSortScore,
        onScore,
        onRank: (row, fee) =>
          onRank({
            row,
            fee,
            setScoringLoading,
            getRegistrationsByDay,
            handleModalContext,
            onChangeJudge,
          }),
      })}
      data={registrationsToDisplay}
    />
  )
}
